import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../axios/axios";

const initialState = {
  members: [], // Now an array of members
  status: "idle",
  error: null,
};

// Fetch Members Data from Backend
export const fetchMembersData = createAsyncThunk(
  "members/fetchMembersData",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/members");
      return response.data.members || []; // Return the array of members
    } catch (error) {
      console.error("Error fetching members data:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Save or Update Members Data to Backend
export const saveMembersToBackend = createAsyncThunk(
  "members/saveMembersToBackend",
  async ({ id, membersData }, { rejectWithValue, dispatch }) => {
    try {
      const formData = new FormData();
      formData.append("name", membersData.name);
      formData.append("post", membersData.post);
      formData.append("location", membersData.location);
      membersData.images.forEach((image) => {
        if (image instanceof File) {
          formData.append("images", image);
        }
      });
      if (membersData.removeImages?.length > 0) {
        formData.append(
          "removeImages",
          JSON.stringify(membersData.removeImages)
        );
      }

      let response;
      if (id) {
        // Update existing member (PATCH)
        response = await api.patch(`/members/update/${id}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      } else {
        // Create new member (POST)
        response = await api.post("/members", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      }

      dispatch(fetchMembersData()); // Re-fetch members after save/update
      return response.data;
    } catch (error) {
      console.error("Error saving members data:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Delete Member
export const deleteMember = createAsyncThunk(
  "members/deleteMember",
  async (id, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/members/delete/${id}`);
      dispatch(fetchMembersData()); // Re-fetch members after deletion
      return id;
    } catch (error) {
      console.error("Error deleting member:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const membersSlice = createSlice({
  name: "members",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMembersData.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchMembersData.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.members = action.payload; // Set the array of members
      })
      .addCase(fetchMembersData.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(deleteMember.fulfilled, (state, action) => {
        state.members = state.members.filter(
          (member) => member._id !== action.payload
        );
      });
  },
});

export default membersSlice.reducer;
